import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { Plus, Pencil, Trash2, RefreshCw, FolderPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDecorCatalog = () => {
  const { getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showItemModal, setShowItemModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [itemForm, setItemForm] = useState({
    category_id: "",
    category_name: "",
    item_name: "",
    tariff: 0,
    is_active: true,
    sort_order: 0
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/decor-items`),
        axios.get(`${API}/decor-categories`)
      ]);
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Failed to load decor catalog");
    } finally {
      setLoading(false);
    }
  };

  // Group items by category
  const itemsByCategory = categories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category_id === cat.id)
  }));

  // Item CRUD
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    
    // Set category name from selected category
    const category = categories.find(c => c.id === itemForm.category_id);
    const data = {
      ...itemForm,
      category_name: category?.name || ""
    };
    
    try {
      if (editingItem) {
        await axios.put(`${API}/decor-items/${editingItem.id}`, data, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Decor item updated");
      } else {
        await axios.post(`${API}/decor-items`, data, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Decor item created");
      }
      
      setShowItemModal(false);
      setEditingItem(null);
      resetItemForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      category_id: item.category_id,
      category_name: item.category_name,
      item_name: item.item_name,
      tariff: item.tariff,
      is_active: item.is_active,
      sort_order: item.sort_order
    });
    setShowItemModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Delete this decor item?")) return;
    
    try {
      await axios.delete(`${API}/decor-items/${itemId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const resetItemForm = () => {
    setItemForm({
      category_id: "",
      category_name: "",
      item_name: "",
      tariff: 0,
      is_active: true,
      sort_order: 0
    });
  };

  // Category CRUD
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/decor-categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/decor-categories`, categoryForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Category created");
      }
      
      setShowCategoryModal(false);
      setEditingCategory(null);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      sort_order: category.sort_order,
      is_active: category.is_active
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    
    try {
      await axios.delete(`${API}/decor-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: "",
      sort_order: 0,
      is_active: true
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Decor Catalog">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Decor Catalog">
      <div className="space-y-6" data-testid="decor-catalog-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-muted-foreground">
            {items.length} items across {categories.length} categories
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setEditingCategory(null);
                resetCategoryForm();
                setShowCategoryModal(true);
              }}
              data-testid="add-category-btn"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
            <Button
              onClick={() => {
                setEditingItem(null);
                resetItemForm();
                setShowItemModal(true);
              }}
              data-testid="add-item-btn"
              className="bg-[#B08968] hover:bg-[#9A7558] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Categories Accordion */}
        <div className="bg-white rounded-2xl border border-border/40 shadow-elegant overflow-hidden">
          <Accordion type="multiple" className="w-full">
            {itemsByCategory.map((category) => (
              <AccordionItem key={category.id} value={category.id}>
                <AccordionTrigger 
                  className="px-6 hover:bg-secondary/30"
                  data-testid={`category-${category.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded">
                      {category.items.length} items
                    </span>
                    {!category.is_active && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  {/* Category Actions */}
                  <div className="flex justify-end gap-2 mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditCategory(category)}
                      data-testid={`edit-category-${category.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit Category
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-category-${category.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                  
                  {/* Items Table */}
                  {category.items.length > 0 ? (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Tariff</th>
                          <th>Status</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.items.map((item) => (
                          <tr key={item.id} data-testid={`item-row-${item.id}`}>
                            <td>{item.item_name}</td>
                            <td className="font-medium text-[#B08968]">
                              {formatINR(item.tariff)}
                            </td>
                            <td>
                              {item.is_active ? (
                                <span className="text-green-600 text-xs">Active</span>
                              ) : (
                                <span className="text-amber-600 text-xs">Inactive</span>
                              )}
                            </td>
                            <td className="text-right">
                              <div className="flex justify-end gap-1">
                                <button
                                  onClick={() => handleEditItem(item)}
                                  data-testid={`edit-item-${item.id}`}
                                  className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  data-testid={`delete-item-${item.id}`}
                                  className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">
                      No items in this category
                    </p>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {categories.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>No categories yet. Start by creating a category.</p>
          </div>
        )}
      </div>

      {/* Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent data-testid="item-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingItem ? "Edit Decor Item" : "Add Decor Item"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleItemSubmit} className="space-y-4 mt-4">
            <div className="form-group">
              <Label htmlFor="item-category">Category</Label>
              <Select
                value={itemForm.category_id}
                onValueChange={(value) => setItemForm({...itemForm, category_id: value})}
              >
                <SelectTrigger data-testid="item-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="form-group">
              <Label htmlFor="item-name">Item Name</Label>
              <Input
                id="item-name"
                data-testid="item-name-input"
                required
                value={itemForm.item_name}
                onChange={(e) => setItemForm({...itemForm, item_name: e.target.value})}
                placeholder="e.g., Entrance Gate Arch"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="item-tariff">Tariff (₹)</Label>
              <Input
                id="item-tariff"
                data-testid="item-tariff-input"
                type="number"
                required
                value={itemForm.tariff}
                onChange={(e) => setItemForm({...itemForm, tariff: parseFloat(e.target.value) || 0})}
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="item-sort">Sort Order</Label>
              <Input
                id="item-sort"
                data-testid="item-sort-input"
                type="number"
                value={itemForm.sort_order}
                onChange={(e) => setItemForm({...itemForm, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="item-active">Active</Label>
              <Switch
                id="item-active"
                data-testid="item-active-switch"
                checked={itemForm.is_active}
                onCheckedChange={(checked) => setItemForm({...itemForm, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowItemModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-item-btn"
                className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white"
              >
                {editingItem ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent data-testid="category-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingCategory ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCategorySubmit} className="space-y-4 mt-4">
            <div className="form-group">
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                data-testid="category-name-input"
                required
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                placeholder="e.g., Sound & Lights"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="category-sort">Sort Order</Label>
              <Input
                id="category-sort"
                data-testid="category-sort-input"
                type="number"
                value={categoryForm.sort_order}
                onChange={(e) => setCategoryForm({...categoryForm, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="category-active">Active</Label>
              <Switch
                id="category-active"
                data-testid="category-active-switch"
                checked={categoryForm.is_active}
                onCheckedChange={(checked) => setCategoryForm({...categoryForm, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCategoryModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-category-btn"
                className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white"
              >
                {editingCategory ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminDecorCatalog;
